import { updateBusinessEntity } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  useToggle,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { useEffect, useState } from 'react';
import { RequestCallbackDialog } from '../../../../../Contact/RequestCallbackModal';
import { useNotification } from '../../../../../hooks/useNotification';
import { EditFieldAlert } from '../../../../common/EditFieldAlert';

export const EditBusinessEntityCodeModal = ({
  isOpen,
  onClose,
  businessEntity,
  onSuccess,
  isEditable = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  businessEntity: IBusinessEntity;
  onSuccess?: () => void;
  isEditable: boolean;
}) => {
  const isUpdatingDescription = useToggle();
  const requestCallbackModalVisibility = useToggle();

  const [businessCode, setBusinessCode] = useState<string>(
    businessEntity.generalCorporationInfo.industryCode?.value || ''
  );

  const existingBusinessCode =
    businessEntity?.generalCorporationInfo?.industryCode?.value;

  const industryCodeTitle =
    businessEntity.generalCorporationInfo.industryCode?.displayName;

  useEffect(() => {
    setBusinessCode(existingBusinessCode);
  }, [existingBusinessCode]);

  const isEditMode = !!existingBusinessCode;

  const { setNotification } = useNotification();

  const onSave = async () => {
    isUpdatingDescription.on();

    if (!businessCode || businessCode.trim() === '') {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Business code is required',
      });
      isUpdatingDescription.off();
      return;
    }

    const res = await updateBusinessEntity(businessEntity.id, {
      businessCode,
    });

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update business code',
      });
      isUpdatingDescription.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Business code updated',
    });

    if (onSuccess) await onSuccess();
    onClose();
    isUpdatingDescription.off();
  };

  function isDisabled() {
    // Check if business code is empty
    if (businessCode?.trim() === '') {
      return true;
    }

    // Check if no changes were made
    if (existingBusinessCode === businessCode) {
      return true;
    }

    // Admin override check
    if (isEditable) {
      return false;
    }

    const result =
      !businessCode ||
      existingBusinessCode === businessCode ||
      (isEditMode && !isEditable);

    return result;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent preventCloseOnOutsideClick>
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Business Code' : 'Add Business Code'}
          </DialogTitle>
        </DialogHeader>
        <RequestCallbackDialog
          isOpen={requestCallbackModalVisibility.state}
          onClose={requestCallbackModalVisibility.off}
        />
        <div className="p-3xl space-y-2xl">
          {isEditMode && !isEditable && (
            <EditFieldAlert
              onContactSupport={requestCallbackModalVisibility.on}
              title={industryCodeTitle}
            />
          )}
          <FieldWrapper>
            <Label htmlFor="businessCode">{industryCodeTitle}</Label>
            <Input
              id="businessCode"
              value={businessCode}
              onChange={(e) => setBusinessCode(e.target.value)}
              placeholder={`Enter ${industryCodeTitle || 'Business Code'}`}
              disabled={isEditMode && !isEditable}
            />
          </FieldWrapper>
        </div>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onSave}
            disabled={isDisabled()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
